import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Dialog, Field, Input, Section, Select, Spinner, Switch, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CustomerLoyaltyView, LoyaltySettings, LoyaltySummary } from '../lib/types';
import { percentToBps } from './pricing';

/**
 * Pazarlama → Sadakat programı (F6-13, ADR-094): puan kazanma/harcama oranları, bekleme ve geçerlilik süreleri,
 * VIP seviyeleri ve program özeti. Müşteri sayfasındaki "Sadakat" bölümü de buradadır. Program varsayılan
 * kapalıdır; açmak ve değiştirmek iki adımlı doğrulama ister.
 */

const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'];
const int = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : -1);
const pointsFmt = (n: number, locale: string) =>
  new Intl.NumberFormat(locale === 'tr' ? 'tr-TR' : 'en-GB', { maximumFractionDigits: 0 }).format(n);

interface TierDraft {
  name: string;
  minSpend: string;
  discount: string;
  freeShipping: boolean;
}
interface Draft {
  enabled: boolean;
  currency: string;
  earn: string;
  value: string;
  minRedeem: string;
  holdDays: string;
  expiryMonths: string;
  tiers: TierDraft[];
}

function draftOf(s: LoyaltySettings): Draft {
  return {
    enabled: s.enabled,
    currency: s.currency,
    earn: String(s.earnPointsPerUnit),
    value: String(s.pointsPerUnitValue),
    minRedeem: String(s.minRedeemPoints),
    holdDays: String(s.holdDays),
    expiryMonths: String(s.expiryMonths),
    tiers: s.tiers.map((x) => ({
      name: x.name,
      minSpend: minorToInput(x.minSpendMinor),
      discount: bpsLabel(x.discountBps).replace('%', ''),
      freeShipping: x.freeShipping,
    })),
  };
}

function bodyOf(d: Draft) {
  return {
    enabled: d.enabled,
    currency: d.currency,
    earnPointsPerUnit: int(d.earn),
    pointsPerUnitValue: int(d.value),
    minRedeemPoints: int(d.minRedeem),
    holdDays: int(d.holdDays),
    expiryMonths: int(d.expiryMonths),
    tiers: d.tiers.map((x) => ({
      name: x.name.trim(),
      minSpendMinor: x.minSpend.trim() ? (toMinor(x.minSpend, d.currency) ?? -1) : 0,
      discountBps: x.discount.trim() ? (percentToBps(x.discount) ?? -1) : 0,
      freeShipping: x.freeShipping,
    })),
  };
}

export function LoyaltyPage() {
  const t = useT();
  const settings = useQuery({
    queryKey: ['loyalty-settings'],
    queryFn: () => api.get<LoyaltySettings>('/v1/admin/loyalty/settings'),
  });
  const summary = useQuery({
    queryKey: ['loyalty-summary'],
    queryFn: () => api.get<LoyaltySummary>('/v1/admin/loyalty/summary'),
  });
  if (!settings.data) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.loyalty">
      <div className="space-y-6">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.loyalty.lead')}</p>
        {summary.data && <SummarySection s={summary.data} />}
        <SettingsForm key={JSON.stringify(settings.data)} saved={settings.data} />
      </div>
    </PageFrame>
  );
}

function SummarySection({ s }: { s: LoyaltySummary }) {
  const t = useT();
  const money = useMoney();
  const { locale } = useI18n();
  const stats = [
    { label: t('admin.loyalty.members'), value: pointsFmt(s.membersWithPoints, locale) },
    {
      label: t('admin.loyalty.outstanding'),
      value: `${pointsFmt(s.outstandingPoints, locale)} · ${money(s.outstandingValueMinor, s.currency)}`,
    },
    { label: t('admin.loyalty.earned30'), value: pointsFmt(s.last30Days.earned, locale) },
    { label: t('admin.loyalty.redeemed30'), value: pointsFmt(s.last30Days.redeemed, locale) },
    { label: t('admin.loyalty.expired30'), value: pointsFmt(s.last30Days.expired, locale) },
  ];
  return (
    <Section
      title={t('admin.loyalty.summary')}
      action={
        <Badge tone={s.enabled ? 'moss' : 'neutral'}>
          {s.enabled ? t('admin.loyalty.on') : t('admin.loyalty.off')}
        </Badge>
      }
    >
      <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-label={t('admin.loyalty.summary')}>
        {stats.map((x) => (
          <div key={x.label} className="rounded-md border border-line bg-surface px-3 py-2">
            <dt className="text-xs text-ink-3">{x.label}</dt>
            <dd className="mt-0.5 text-[0.9375rem] font-medium tabular">{x.value}</dd>
          </div>
        ))}
      </dl>
      {s.tiers.length > 0 && (
        <p className="mt-3 text-sm text-ink-2">
          {t('admin.loyalty.tierCounts', {
            list: s.tiers.map((x) => `${x.name}: ${pointsFmt(x.customers, locale)}`).join(' · '),
          })}
        </p>
      )}
    </Section>
  );
}

function SettingsForm({ saved }: { saved: LoyaltySettings }) {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const canManage = can('marketing.loyalty.manage');
  const [d, setD] = useState<Draft>(() => draftOf(saved));
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const setTier = (i: number, p: Partial<TierDraft>) =>
    setD((x) => ({ ...x, tiers: x.tiers.map((y, j) => (j === i ? { ...y, ...p } : y)) }));
  const dirty = JSON.stringify(d) !== JSON.stringify(draftOf(saved));
  const save = useApiMutation(() => api.put<LoyaltySettings>('/v1/admin/loyalty/settings', bodyOf(d)), {
    invalidate: [['loyalty-settings'], ['loyalty-summary']],
    success: t('admin.loyalty.saved'),
    silentFieldErrors: true,
  });
  const err = (f: string) => fieldError(save.error, f);
  const earn = int(d.earn);
  const value = int(d.value);
  const backBps = earn > 0 && value > 0 ? Math.floor((earn * 10_000) / value) : 0;
  const turningOn = d.enabled && !saved.enabled;
  return (
    <Section title={t('admin.loyalty.settings')} description={t('admin.loyalty.settingsLead')}>
      <fieldset disabled={!canManage} className="grid max-w-3xl gap-4">
        <div className="flex items-center gap-2">
          <Switch
            id="loyalty-enabled"
            checked={d.enabled}
            onCheckedChange={(v) => set({ enabled: v })}
            disabled={!canManage}
          />
          <label htmlFor="loyalty-enabled" className="text-sm font-medium">
            {t('admin.loyalty.enabled')}
          </label>
          <HelpTip k="loyalty.enabled" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('admin.loyalty.currency')} help={<HelpTip k="loyalty.currency" />}>
            {(p) => (
              <Select {...p} value={d.currency} onChange={(e) => set({ currency: e.target.value })}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.loyalty.earn')}
            help={<HelpTip k="loyalty.earn" />}
            error={err('earnPointsPerUnit')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={d.earn}
                onChange={(e) => set({ earn: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.loyalty.value')}
            help={<HelpTip k="loyalty.value" />}
            error={err('pointsPerUnitValue')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={d.value}
                onChange={(e) => set({ value: e.target.value })}
              />
            )}
          </Field>
        </div>
        <p className="rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm" role="status">
          {earn > 0 && value > 0
            ? t('admin.loyalty.preview', {
                one: money(100, d.currency),
                earn,
                value,
                percent: bpsLabel(backBps),
              })
            : t('admin.loyalty.previewNoEarn')}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label={t('admin.loyalty.minRedeem')}
            help={<HelpTip k="loyalty.min_redeem" />}
            error={err('minRedeemPoints')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={d.minRedeem}
                onChange={(e) => set({ minRedeem: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.loyalty.holdDays')}
            help={<HelpTip k="loyalty.hold_days" />}
            error={err('holdDays')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={d.holdDays}
                onChange={(e) => set({ holdDays: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.loyalty.expiryMonths')}
            help={<HelpTip k="loyalty.expiry" />}
            description={t('admin.loyalty.expiryHint')}
            error={err('expiryMonths')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={d.expiryMonths}
                onChange={(e) => set({ expiryMonths: e.target.value })}
              />
            )}
          </Field>
        </div>

        <fieldset className="space-y-3">
          <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
            {t('admin.loyalty.tiers')}
            <HelpTip k="loyalty.tiers" />
          </legend>
          <p className="text-sm text-ink-2">{t('admin.loyalty.tiersLead')}</p>
          {d.tiers.map((x, i) => (
            <div
              key={i}
              className="grid items-end gap-3 rounded-md border border-line p-3 sm:grid-cols-[1fr_9rem_7rem_auto_auto]"
            >
              <Field
                label={t('admin.loyalty.tierName')}
                help={<HelpTip k="loyalty.tier_name" />}
                error={err(`tiers.${i}.name`)}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={x.name}
                    maxLength={40}
                    onChange={(e) => setTier(i, { name: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('admin.loyalty.tierSpend', { currency: d.currency })}
                help={<HelpTip k="loyalty.tier_spend" />}
                error={err(`tiers.${i}.minSpendMinor`)}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="decimal"
                    className="tabular"
                    value={x.minSpend}
                    onChange={(e) => setTier(i, { minSpend: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('admin.loyalty.tierDiscount')}
                help={<HelpTip k="loyalty.tier_discount" />}
                error={err(`tiers.${i}.discountBps`)}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="decimal"
                    className="tabular"
                    value={x.discount}
                    onChange={(e) => setTier(i, { discount: e.target.value })}
                  />
                )}
              </Field>
              <label className="flex h-10 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--zt-pine)]"
                  checked={x.freeShipping}
                  onChange={(e) => setTier(i, { freeShipping: e.target.checked })}
                />
                {t('admin.loyalty.tierShipping')}
                <HelpTip k="loyalty.tier_shipping" />
              </label>
              <span className="flex h-10 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('admin.loyalty.tierRemove', { name: x.name || String(i + 1) })}
                  onClick={() => set({ tiers: d.tiers.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
                <HelpTip k="loyalty.tier_remove" />
              </span>
            </div>
          ))}
          {d.tiers.length < 5 && (
            <div className="flex items-center gap-1">
              <Button
                onClick={() =>
                  set({ tiers: [...d.tiers, { name: '', minSpend: '', discount: '', freeShipping: false }] })
                }
              >
                <Plus className="h-4 w-4" aria-hidden />
                {t('admin.loyalty.tierAdd')}
              </Button>
              <HelpTip k="loyalty.tier_add" />
            </div>
          )}
        </fieldset>
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
        {save.error && save.error.errors.length > 0 && (
          <p role="alert" className="text-sm text-rose">
            {t('admin.loyalty.fixErrors')}
          </p>
        )}
      </fieldset>
      {canManage && (
        <div className="mt-4 flex items-center gap-1">
          {turningOn ? (
            <ConfirmButton
              variant="primary"
              size="md"
              disabled={!dirty}
              message={t('admin.loyalty.enableConfirm')}
              onConfirm={() => save.mutateAsync(undefined).catch(() => undefined)}
            >
              {t('admin.loyalty.save')}
            </ConfirmButton>
          ) : (
            <Button
              variant="primary"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(undefined)}
            >
              {t('admin.loyalty.save')}
            </Button>
          )}
          <HelpTip k="loyalty.save" />
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------- Müşteri sayfası

/** Müşteri → Sadakat: seviye, puan, hareketler ve gerekçeli puan ekleme/düşme. */
export function CustomerLoyaltySection({
  customerId,
  anonymized,
}: {
  customerId: string;
  anonymized: boolean;
}) {
  const t = useT();
  const money = useMoney();
  const { formatDate, locale } = useI18n();
  const { can } = usePermissions();
  const [adjusting, setAdjusting] = useState(false);
  const q = useQuery({
    queryKey: ['customer-loyalty', customerId],
    queryFn: () => api.get<CustomerLoyaltyView>(`/v1/admin/customers/${customerId}/loyalty`),
  });
  const v = q.data;
  if (!v || (!v.enabled && !v.transactions.length)) return null;
  const canManage = can('customers.credit.manage') && !anonymized;
  return (
    <Section
      title={t('admin.loyalty.customerTitle')}
      action={
        canManage && (
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => setAdjusting(true)}>
              {t('admin.loyalty.adjust')}
            </Button>
            <HelpTip k="customers.loyalty_adjust" />
          </div>
        )
      }
    >
      <div className="space-y-3 text-sm">
        <p data-testid="loyalty-balance">
          <span className="text-lg font-medium tabular">
            {t('admin.loyalty.points', { points: pointsFmt(v.points.balance, locale) })}
          </span>{' '}
          <span className="text-ink-3">({money(v.points.valueMinor, v.currency)})</span>
        </p>
        {v.points.expiringSoon > 0 && (
          <p className="text-amber">
            {t('admin.loyalty.expiringSoon', { points: pointsFmt(v.points.expiringSoon, locale) })}
          </p>
        )}
        <p className="text-ink-2">
          {v.tier
            ? t('admin.loyalty.customerTier', { tier: v.tier.name })
            : t('admin.loyalty.customerNoTier')}{' '}
          · {t('admin.loyalty.spend12', { amount: money(v.spend12MonthsMinor, v.currency) })}
          {v.nextTier &&
            ` · ${t('admin.loyalty.nextTier', { tier: v.nextTier.name, amount: money(v.nextTier.missingMinor, v.currency) })}`}
        </p>
        {v.transactions.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[34rem] text-sm" aria-label={t('admin.loyalty.history')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.loyalty.colDate')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.loyalty.colKind')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.loyalty.colPoints')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.loyalty.colExpires')}</th>
                </tr>
              </thead>
              <tbody>
                {v.transactions.map((x) => (
                  <tr key={x.id} className="border-t border-line align-top">
                    <td className="px-3 py-2 text-ink-2">{formatDate(x.createdAt, true)}</td>
                    <td className="px-3 py-2">
                      {t(`admin.loyalty.kinds.${x.kind}`)}
                      {x.orderId && x.orderNumber && (
                        <>
                          {' · '}
                          <Link
                            to="/siparisler/$id"
                            params={{ id: x.orderId }}
                            className="text-pine underline underline-offset-2 hover:no-underline"
                          >
                            {x.orderNumber}
                          </Link>
                        </>
                      )}
                      {x.note && <p className="text-ink-3">{x.note}</p>}
                    </td>
                    <td className={`px-3 py-2 text-end tabular ${x.points < 0 ? 'text-ink-2' : 'text-moss'}`}>
                      {x.points > 0 ? '+' : '−'}
                      {pointsFmt(Math.abs(x.points), locale)}
                    </td>
                    <td className="px-3 py-2 text-end text-ink-3">
                      {x.points > 0 && x.expiresAt ? formatDate(x.expiresAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {adjusting && (
        <AdjustPointsDialog
          customerId={customerId}
          balance={v.points.balance}
          onClose={() => setAdjusting(false)}
        />
      )}
    </Section>
  );
}

function AdjustPointsDialog({
  customerId,
  balance,
  onClose,
}: {
  customerId: string;
  balance: number;
  onClose: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');
  const [points, setPoints] = useState('');
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const n = int(points);
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/customers/${customerId}/loyalty`,
        { points: direction === 'add' ? n : -n, note: note.trim() },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['customer-loyalty', customerId], ['loyalty-summary']],
      success: t('admin.loyalty.adjusted'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const ready = n > 0 && note.trim().length >= 3;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.loyalty.adjustTitle')}
      description={t('admin.loyalty.adjustLead', { points: pointsFmt(balance, locale) })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={direction === 'add' ? 'primary' : 'danger'}
            loading={m.isPending}
            disabled={!ready}
            onClick={() => m.mutate(undefined)}
          >
            {direction === 'add' ? t('admin.loyalty.adjustAdd') : t('admin.loyalty.adjustSubtract')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.loyalty.direction')} help={<HelpTip k="tenders.adjust_direction" />}>
          {(p) => (
            <Select
              {...p}
              value={direction}
              onChange={(e) => setDirection(e.target.value as typeof direction)}
            >
              <option value="add">{t('admin.loyalty.directionAdd')}</option>
              <option value="subtract">{t('admin.loyalty.directionSubtract')}</option>
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.loyalty.pointsField')}
          required
          help={<HelpTip k="loyalty.adjust_points" />}
          error={
            fieldError(m.error, 'points') ??
            (points.trim() && n <= 0 ? t('admin.loyalty.pointsInvalid') : undefined)
          }
        >
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              className="tabular"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('admin.giftCards.adjustNote')}
          required
          help={<HelpTip k="tenders.adjust_note" />}
          description={t('admin.giftCards.adjustNoteHint')}
          error={fieldError(m.error, 'note')}
        >
          {(p) => (
            <Textarea
              {...p}
              className="min-h-16"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}
